import { FlexView, Row, Separator } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import { Table, TableCell } from 'comps';
import Pagination from 'modules/list/pagination';
import React, { useCallback, useEffect, useState } from 'react';
import { Menu, TableRow } from 'semantic-ui-react';
import styled from 'styled-components';

import COLORS from '../../../constants/colors';
import { useGetAllEvals, useGetAllEvalSpecialistRoster } from 'actions/eval-reporting';
import {
  Chip,
  ErrorLine,
  FilterRow,
  MonoText,
  MonthPicker,
  PageHeader,
  RegionPicker,
  SpecialistPicker,
} from '../common/filters';
import {
  EVAL_STATUSES,
  currentMonthValue,
  describeEvalScore,
  formatIstDateTime,
} from '../common/constants';

const PER_PAGE = 20;

const Container = styled(FlexView)`
  padding: 0 16px;
  width: 100%;
`;
const TableWrapper = styled(Row)`
  margin-top: 16px;
  width: 100%;
`;
const PaginationWrapper = styled(Row)`
  height: 42px;
`;

const STATUS_TABS = [
  { key: EVAL_STATUSES.upcoming, label: 'Upcoming' },
  { key: EVAL_STATUSES.completed, label: 'Completed' },
];

const stateColor = state => {
  if (!state) return COLORS.GRAY;
  if (state === 'DONE') return COLORS.DARK_GREEN;
  if (state === 'CANCELLED' || state.endsWith('NO_SHOW')) return COLORS.RUSTY_RED;

  return COLORS.GRAY;
};

const Evals = () => {
  const [status, setStatus] = useState(EVAL_STATUSES.upcoming);
  const [month, setMonth] = useState(currentMonthValue);
  const [teacherId, setTeacherId] = useState(null);
  const [region, setRegion] = useState(null);
  const [page, setPage] = useState(1);

  const {
    data,
    getAll: getEvals,
    isProcessing: isLoading,
    error: evalsError,
  } = useGetAllEvals();

  const { data: rosterData, getAll: getRoster } = useGetAllEvalSpecialistRoster();

  useEffect(() => {
    getRoster({ month });
  }, [getRoster, month]);

  const fetchEvals = useCallback(() => {
    const query = { status, month, page, per_page: PER_PAGE };

    if (teacherId) query.teacher_id = teacherId;
    if (region) query.region = region;
    getEvals(query);
  }, [getEvals, status, month, teacherId, region, page]);

  useEffect(() => {
    fetchEvals();
  }, [fetchEvals]);

  const handleStatusChange = useCallback(nextStatus => {
    setPage(1);
    setStatus(nextStatus);
  }, []);

  const handleMonthChange = useCallback(nextMonth => {
    setPage(1);
    setMonth(nextMonth);
  }, []);

  const handleSpecialistChange = useCallback(nextTeacherId => {
    setPage(1);
    setTeacherId(nextTeacherId);
  }, []);

  const handleRegionChange = useCallback(nextRegion => {
    setPage(1);
    setRegion(nextRegion);
  }, []);

  const results = data?.evals || [];
  const specialists = rosterData?.specialists || [];
  const isCompleted = status === EVAL_STATUSES.completed;
  const total = data?.pagination?.total ?? null;
  const showPagination = results.length > 0;

  return (
    <Container>
      <PageHeader title="Evals" />
      <Menu pointing secondary>
        {STATUS_TABS.map(tab => (
          <Menu.Item
            key={tab.key}
            name={tab.label}
            active={status === tab.key}
            onClick={() => handleStatusChange(tab.key)}
          />
        ))}
      </Menu>

      <FilterRow justifyContent="space-between" alignItems="center">
        <FilterRow alignItems="center">
          <MonthPicker value={month} onChange={handleMonthChange} />
          <SpecialistPicker
            specialists={specialists}
            value={teacherId}
            onChange={handleSpecialistChange}
          />
          <RegionPicker
            value={region}
            onChange={handleRegionChange}
            subject="opportunity"
          />
        </FilterRow>
        <Text variant="body3" color={COLORS.GRAY}>
          {total ? `${total} eval${total === 1 ? '' : 's'}` : ''}
        </Text>
      </FilterRow>

      <ErrorLine error={evalsError} />

      <TableWrapper>
        <Table
          headerCell={[
            { title: 'Scheduled (IST)' },
            { title: 'Student' },
            { title: 'Specialist' },
            { title: 'Specialist ID' },
            { title: 'State' },
            ...(isCompleted ? [{ title: 'Score' }] : []),
          ]}
          dataLength={results.length}
          havePagination
          showInfo={false}
          showLoader={isLoading}
          flexBasis="100%"
          noDataMessage={
            isCompleted
              ? 'No completed evals for this filter.'
              : 'No upcoming evals for this filter.'
          }
          scrollBodyHeight={isLoading || !showPagination ? '532px' : 'calc(100vh - 210px)'}
        >
          {results.map(evalRow => (
            <TableRow key={evalRow.assessment_id || evalRow.student_demo_id}>
              <TableCell width={3} textTransform="none">
                <MonoText>{formatIstDateTime(evalRow.scheduled_start_time)}</MonoText>
              </TableCell>
              <TableCell width={3} textTransform="none">
                {evalRow.student_name || '-'}
              </TableCell>
              <TableCell width={3} textTransform="none">
                {evalRow.teacher_name || '-'}
              </TableCell>
              <TableCell width={3} textTransform="none">
                <MonoText>{evalRow.teacher_id || '-'}</MonoText>
              </TableCell>
              <TableCell width={2}>
                <Chip color={stateColor(evalRow.demo_state)}>{evalRow.demo_state || '-'}</Chip>
              </TableCell>
              {isCompleted && (
                <TableCell width={2} textTransform="none">
                  {describeEvalScore(evalRow.eval_scores)}
                </TableCell>
              )}
            </TableRow>
          ))}
        </Table>
      </TableWrapper>

      <Separator transparent height={8} />
      <PaginationWrapper justifyContent="center" alignItems="center">
        {showPagination && (
          <Pagination
            page={page}
            perPage={PER_PAGE}
            count={total}
            onPageChange={setPage}
            shouldShowNewUi
          />
        )}
      </PaginationWrapper>
    </Container>
  );
};

export default Evals;
